// SidebarPrimary — 80px primary rail. Clone từ Core's SidebarPrimary.jsx.
// Strip:
//   - User settings modal (showUserMenu) — Core-specific theme/chat/status preferences
//   - MiniCallBubble (VoIP — Core)
//   - useChatPreferences (Core's team_chat module)
// Keep: visual 80px gradient bg-slate-950, module icons, active highlight, avatar bottom.

import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Menu } from 'lucide-react';
import clsx from 'clsx';
import { tLabel } from '../../../i18n/runtime.js';
import { findActiveGroup, filterMenuByPermissions } from '../../../navigation/menuUtils.js';
import { useAuth } from '@host/auth';

import { useLingui } from '@lingui/react/macro';

import { msg } from '@lingui/core/macro';import { i18n } from '../../../i18n/runtime.js';

// Mapping menu group key → human-readable suffix cho badge title (tooltip).
// Khớp Core's SidebarPrimary.jsx — giữ nguyên thứ tự key để tooltip không lệch.
const BADGE_TITLE_BY_KEY = {
  team_chat:   msg`tin mới`,
  tasks:       msg`task quá hạn`,
  approvals:   msg`đơn cần duyệt`,
  tickets:     msg`ticket cần xử lý`,     // Core menu key
  support:     msg`ticket cần xử lý`,     // SaaS menu key
  recruitment: msg`đề xuất chờ duyệt`,
  // Core + SaaS: hội thoại CÓ TIN CHƯA ĐỌC của chính người đang đăng nhập (kernel
  // inbox-counts.unread_mine). Sếp Phú chốt 08/08 sau khi badge nhóm (của tôi + cầu cứu)
  // chọi badge con (chỉ cầu cứu) — nay 2 chỗ cùng một số.
  chat:        msg`hội thoại chưa đọc`,
};

// Nhãn nhóm trên rail 80px. Ô chữ hẹp (54px, cộng phần đệm của nút là 62px) mà span lại co
// theo từ dài nhất và căn giữa ⇒ một từ dài hơn 62px tràn ĐỀU HAI BÊN rồi bị `overflow-hidden`
// của nút xén mất hai mép: FR «Approbations» (66px) hiện «pprobations», «Automatisation» (74px)
// hiện «utomatisatio» (hub Work 19/10/2026). Nhãn VI dài nhất chỉ ~57px nên trước giờ không lộ.
// Cách sửa: đo từ dài nhất; từ nào không vừa khung trong của nút thì cho ngắt có gạch nối theo
// ngôn ngữ đang chọn («Approba-/tions», `lang` để trình duyệt tra đúng từ điển) và tối đa 2 dòng
// — giống luật «nhãn dài xuống 2 dòng» của giao diện v2. Nhãn vừa ô (mọi nhãn VI/EN hiện có)
// giữ NGUYÊN markup cũ, không đổi một điểm ảnh. Đo bằng canvas chứ không đo DOM: số đo không phụ
// thuộc class vừa đổi ⇒ không có vòng đo → đổi class → đo lại.
let railMeasureCtx = null;
function widestWordPx(text, el) {
  if (!railMeasureCtx) railMeasureCtx = document.createElement('canvas').getContext('2d');
  if (!railMeasureCtx) return 0;
  const cs = getComputedStyle(el);
  railMeasureCtx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  return String(text).split(/\s+/).reduce((max, w) => Math.max(max, railMeasureCtx.measureText(w).width), 0);
}

function RailLabel({ text, lang }) {
  const ref = useRef(null);
  const [tooWide, setTooWide] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    const btn = el?.parentElement;
    if (!el || !btn) return undefined;
    let alive = true;
    const check = () => {
      if (!alive) return;
      // clientWidth = khung trong của nút (gồm phần đệm, trừ viền) — đúng vùng nút còn cho hiện.
      const room = btn.clientWidth;
      if (!room) return; // chưa có bố cục (ẩn / môi trường test)
      setTooWide(widestWordPx(text, el) > room);
    };
    check();
    // Thanh cuộn dọc (Windows) ăn bớt bề ngang rail · phông web tải xong thì số đo đổi.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(btn);
    document.fonts?.ready?.then(check);
    return () => { alive = false; ro?.disconnect(); };
  }, [text]);
  return (
    <span
      ref={ref}
      lang={lang}
      // Giữ class `line-clamp-1`: ui-v2.css bắt đúng chuỗi này để nới 2 dòng + line-height 1.2.
      className={clsx('leading-tight text-center font-medium line-clamp-1', tooWide && 'self-stretch break-words hyphens-auto')}
      style={tooWide ? { WebkitLineClamp: 2, lineClamp: 2 } : undefined}
    >
      {text}
    </span>
  );
}

export default function SidebarPrimary({
  menu = [],
  brand = {},
  onSelectGroup,
  onOpenMobile,
  homeLink,
  collapsed = false,
  badgeCounts = {},
  // Slot pattern — consumer (Core/SaaS) override các zone Core-specific:
  //   topSlot:    render trên cùng (vd MiniCallBubble — VoIP, Core only)
  //   bottomSlot: render thay default avatar (Core dùng custom user menu modal với theme/chat preferences)
  topSlot,
  bottomSlot,
}) {
  const {
    t
  } = useLingui();

  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const activeGroup = findActiveGroup(menu, location.pathname);
  const filteredMenu = filterMenuByPermissions(menu, user?.permissions);

  return (
    <div className={clsx('h-full bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-white border-r border-slate-800/80 flex flex-col transition-all duration-300 ease-out w-[80px]')}>
      {topSlot}
      {/* Logo */}
      <div className="h-[68px] border-b border-slate-800/80 flex items-center justify-center relative shrink-0">
        {onOpenMobile && (
          <button className="lg:hidden absolute left-2 text-slate-300 hover:text-white" onClick={onOpenMobile}>
            <Menu size={18} />
          </button>
        )}
        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shadow-[0_6px_20px_rgba(15,23,42,0.35)]">
          {brand.logo ? (
            <img src={brand.logo} alt={brand.appName || 'App'} className="w-7 h-7 object-contain" />
          ) : (
            <span className="text-sm font-bold text-white">{(brand.appShort || brand.appName || 'C')[0]?.toUpperCase()}</span>
          )}
        </div>
      </div>

      {/* Module icons */}
      <div className="px-2 py-3 space-y-1.5 overflow-y-auto flex-1">
        {homeLink && (
          <Link
            to={homeLink.to || '/'}
            className="group flex flex-col items-center gap-1 px-1 py-2.5 rounded-xl text-[10px] text-slate-300 hover:bg-white/5 hover:text-white transition"
            title={tLabel(homeLink.label) || t`Trang chủ`}
          >
            <Home size={17} />
            {!collapsed && <span className="leading-none text-center font-medium">{homeLink.shortLabel || 'Home'}</span>}
          </Link>
        )}

        {filteredMenu.map((group) => {
          const Icon = group.icon;
          const isActive = activeGroup?.key === group.key;
          const target = group.path || group.basePath;
          const singleLink = !!(target && !group.items?.length);
          const handleClick = () => {
            if (singleLink) { navigate(target); return; }
            onSelectGroup?.(group);
            // Click group có items → vừa mở submenu vừa navigate vào page đầu tiên.
            // Trước đây chỉ mở submenu nên main content giữ nguyên trang cũ (Bug #1/2/4
            // 2026-05-14: "Tổng quan/Nhân sự/Hệ thống" click không chuyển trang).
            // Bỏ qua nếu đang ở trong group (tránh remount page hiện tại).
            if (isActive) return;
            const firstPath = group.items?.[0]?.path;
            if (firstPath) navigate(firstPath);
          };
          // Badge count: caller pass badgeCounts={{ [groupKey]: number }}.
          // Render rose pill ở top-right icon (giống Core SidebarPrimary.jsx).
          const badgeCount = Number(badgeCounts?.[group.key]) || 0;
          // ⚠️ Phải kiểm CÓ descriptor rồi mới dịch. Bảng trên chỉ có 7 nhóm mang huy hiệu,
          // còn thanh bên có cả chục nhóm ⇒ `i18n._(undefined)` cho phần còn lại, và Lingui
          // rải cảnh báo "Uncompiled message detected" mỗi lần vẽ lại (98 lần chỉ trong một
          // lần chuyển trang — đủ để dìm chết mọi log thật trong console).
          const badgeMsg = BADGE_TITLE_BY_KEY[group.key];
          const badgeSuffix = badgeMsg ? i18n._(badgeMsg) : '';
          const tooltip = badgeCount > 0 && badgeSuffix
            ? `${tLabel(group.label)} (${badgeCount} ${badgeSuffix})`
            : tLabel(group.label);
          return (
            <button
              key={group.key}
              onClick={handleClick}
              title={tooltip}
              className={clsx(
                'relative w-full flex flex-col items-center gap-1 px-1 py-2.5 rounded-xl transition-all duration-200 text-[10px] border overflow-hidden',
                isActive
                  ? 'bg-gradient-to-b from-blue-500/20 to-blue-500/10 text-blue-200 border-blue-400/30 shadow-[0_8px_22px_rgba(59,130,246,0.18)]'
                  : 'text-slate-300 border-transparent hover:bg-white/5 hover:text-white',
              )}
            >
              {isActive && <span className="absolute left-0 top-2.5 bottom-2.5 w-0.5 rounded-r-full bg-blue-400" />}
              <div className="relative">
                {Icon && <Icon size={17} />}
                {badgeCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-[16px] text-center shadow-md ring-1 ring-slate-900/60">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </div>
              {!collapsed && <RailLabel text={tLabel(group.label)} lang={i18n.locale} />}
            </button>
          );
        })}
      </div>

      {/* Bottom slot — Core dùng custom user menu modal (theme/chat preferences/voip/logout).
          SaaS không pass → default avatar link → /me page. */}
      {bottomSlot !== undefined ? bottomSlot : (
        <div className="p-2 border-t border-slate-800/80 shrink-0">
          <div className="relative w-fit mx-auto">
            <button
              onClick={() => navigate('/me')}
              className="flex items-center justify-center rounded-full hover:opacity-80 transition"
              title={t`${user?.name || user?.display_name || user?.email || 'User'} — Cài đặt cá nhân`}
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user?.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-white/10" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-sm font-bold shadow-md ring-2 ring-white/10">
                  {(user?.name || user?.display_name || user?.email || 'U')[0]?.toUpperCase()}
                </div>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
